import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { DraftsPanel, saveDraft, discardDraft } from "@/components/DraftsPanel";
import { DocNumberField } from "@/components/sales/InvoiceNumberField";
import { fmtINR, fmtDate, today } from "@/lib/format";
import { Coins, ArrowDown, ArrowUp } from "lucide-react";

export default function Payments() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [detailRow, setDetailRow] = useState(null);
  const [detailVoucher, setDetailVoucher] = useState(null);
  const [kind, setKind] = useState("receipt");
  const [parties, setParties] = useState([]);
  const [banks, setBanks] = useState([]);
  const [branches, setBranches] = useState([]);
  const [selectedParty, setSelectedParty] = useState("");
  const [openDocs, setOpenDocs] = useState([]);
  const [form, setForm] = useState({ date: today(), bank_account_id: "", amount: 0, reference: "", narration: "", allocations: {} });

  const load = async () => setRows((await api.get("/payments")).data);
  useEffect(() => { load(); }, []);
  useEffect(() => {
    api.get("/parties").then((r) => setParties(r.data));
    api.get("/accounts").then((r) => setBanks(r.data.filter((a) => ["bank", "cash"].includes(a.subtype))));
    api.get("/branches").then((r) => setBranches(r.data));
  }, []);

  const [draftId, setDraftId] = useState(null);
  const [draftsKey, setDraftsKey] = useState(0);
  const buildPayload = () => ({
    kind, date: form.date, party_id: selectedParty, bank_account_id: form.bank_account_id, amount: Number(form.amount),
    allocations: openDocs.filter((d) => Number(form.allocations[d.id] || 0) > 0)
      .map((d) => ({ doc_type: d.doc_type, doc_id: d.id, amount: Number(form.allocations[d.id]) })),
    reference: form.reference, narration: form.narration,
    ...(kind === "receipt" && form.voucher_number ? { voucher_number: form.voucher_number } : {}),
  });
  const saveAsDraft = async () => {
    if (!selectedParty) return toast.error("Select a party");
    if (!form.bank_account_id) return toast.error("Select a bank/cash account");
    try {
      await saveDraft(kind, buildPayload(), { form, selectedParty, kind }, draftId);
      setDraftId(null); setOpen(false); setDraftsKey((k) => k + 1);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save draft"); }
  };
  const openDraft = async (d) => {
    setKind(d.ui.kind || d.doc_type); setForm(d.ui.form || d.ui); setDraftId(d.id);
    await loadOpenDocs(d.ui.selectedParty || d.payload.party_id);
    setOpen(true);
  };
  const startNew = (k) => {
    setDraftId(null);
    setKind(k);
    setSelectedParty(""); setOpenDocs([]);
    setForm({ date: today(), bank_account_id: banks[0]?.id || "", amount: 0, reference: "", narration: "", allocations: {} });
    setOpen(true);
  };

  const loadOpenDocs = async (pid) => {
    setSelectedParty(pid);
    if (!pid) return setOpenDocs([]);
    const r = await api.get(`/parties/${pid}/open-docs`);
    setOpenDocs(r.data.docs || []);
  };

  const setAlloc = (docId, amt) => setForm((f) => ({ ...f, allocations: { ...f.allocations, [docId]: amt } }));
  const autoAllocate = () => {
    let remaining = Number(form.amount) || 0;
    const alloc = {};
    for (const d of openDocs) {
      if (remaining <= 0) break;
      const take = Math.min(remaining, d.balance_due);
      if (take > 0) { alloc[d.id] = take; remaining -= take; }
    }
    setForm((f) => ({ ...f, allocations: alloc }));
  };

  const totalAlloc = Object.values(form.allocations).reduce((s, v) => s + Number(v || 0), 0);
  const filteredParties = parties.filter((p) => (kind === "receipt" ? p.party_type === "customer" : p.party_type === "vendor"));

  const submit = async () => {
    if (!selectedParty) return toast.error("Select a party");
    if (!form.bank_account_id) return toast.error("Select a bank/cash account");
    if (Number(form.amount) <= 0) return toast.error("Amount required");
    try {
      await api.post("/payments", buildPayload());
      await discardDraft(draftId); setDraftId(null); setDraftsKey((k) => k + 1);
      toast.success("Posted"); setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const receipts = rows.filter((r) => r.kind === "receipt");
  const payments = rows.filter((r) => r.kind === "payment");

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="payments-page">
      <PageHeader
        eyebrow="Cash Management"
        title="Payments & Receipts"
        description="Record customer receipts and vendor payments with per-invoice allocation. Every entry updates AR/AP and posts a voucher."
        actions={
          <>
            <Button onClick={() => startNew("receipt")} className="rounded-sm bg-accent hover:bg-accent/90 text-white gap-1.5" data-testid="new-receipt-btn"><ArrowDown className="w-3.5 h-3.5" /> New Receipt</Button>
            <Button onClick={() => startNew("payment")} variant="outline" className="rounded-sm gap-1.5" data-testid="new-payment-btn"><ArrowUp className="w-3.5 h-3.5" /> New Payment</Button>
          </>
        }
      />

      <DraftsPanel docType="receipt,payment" refreshKey={draftsKey} onPosted={() => load()} onOpen={openDraft} />

      <Tabs defaultValue="receipts">
        <TabsList className="rounded-sm">
          <TabsTrigger value="receipts" className="rounded-sm">Receipts ({receipts.length})</TabsTrigger>
          <TabsTrigger value="payments" className="rounded-sm">Payments ({payments.length})</TabsTrigger>
        </TabsList>
        {[["receipts", receipts], ["payments", payments]].map(([k, list]) => (
          <TabsContent key={k} value={k} className="mt-4">
            <div className="card-sharp overflow-hidden">
              <table className="w-full dense">
                <thead><tr><th>Date</th><th>Party</th><th>Reference</th><th className="text-right">Amount</th><th className="text-right">Allocated</th><th className="text-right">Unallocated</th></tr></thead>
                <tbody>
                  {list.length === 0 && <tr><td colSpan="6" className="text-center text-muted-foreground py-8">No {k} yet</td></tr>}
                  {list.map((r) => (
                    <tr key={r.id} data-testid={`${k}-row-${r.id}`}
                          className="cursor-pointer hover:bg-muted/40 transition-colors"
                          onClick={async () => {
                            setDetailRow(r); setDetailVoucher(null);
                            if (r.voucher_id) {
                              try {
                                const v = await api.get(`/vouchers/${r.voucher_id}`);
                                setDetailVoucher(v.data);
                              } catch {}
                            }
                          }}>
                      <td className="text-xs">{fmtDate(r.date)}</td>
                      <td>{r.party_name}</td>
                      <td className="text-xs font-mono text-muted-foreground">{r.reference || "—"}</td>
                      <td className="text-right tabular-nums font-medium">{fmtINR(r.amount)}</td>
                      <td className="text-right tabular-nums">{fmtINR(r.amount - r.unallocated)}</td>
                      <td className="text-right tabular-nums">{fmtINR(r.unallocated)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>
        ))}
      </Tabs>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="w-full sm:max-w-3xl rounded-none overflow-y-auto" data-testid="payment-drawer">
          <SheetHeader className="pb-4 border-b">
            <SheetTitle className="font-heading flex items-center gap-2">
              <Coins className="w-4 h-4 text-accent" /> New {kind === "receipt" ? "Receipt" : "Payment"}
            </SheetTitle>
          </SheetHeader>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
            {kind === "receipt" && (
              <DocNumberField kind="receipt" label="Receipt No." open={open}
                value={form.voucher_number} maxLength={30} testId="receipt-number"
                onChange={(v) => setForm((f) => ({ ...f, voucher_number: v }))} />
            )}
            <div>
              <Label className="text-[10px] uppercase font-mono tracking-widest">Party ({kind === "receipt" ? "customer" : "vendor"})</Label>
              <Select value={selectedParty} onValueChange={loadOpenDocs}>
                <SelectTrigger className="rounded-sm mt-1" data-testid="payment-party"><SelectValue placeholder="Select party" /></SelectTrigger>
                <SelectContent className="max-h-72"><div className="max-h-72 overflow-y-auto">{filteredParties.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</div></SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] uppercase font-mono tracking-widest">Bank / Cash</Label>
              <Select value={form.bank_account_id} onValueChange={(v) => setForm({ ...form, bank_account_id: v })}>
                <SelectTrigger className="rounded-sm mt-1" data-testid="payment-bank"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{banks.map((b) => <SelectItem key={b.id} value={b.id}><span className="font-mono text-xs mr-2">{b.code}</span>{b.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] uppercase font-mono tracking-widest">Date</Label>
              <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="rounded-sm mt-1" />
            </div>
            <div>
              <Label className="text-[10px] uppercase font-mono tracking-widest">Amount</Label>
              <Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="rounded-sm mt-1 tabular-nums" data-testid="payment-amount" />
            </div>
            <div>
              <Label className="text-[10px] uppercase font-mono tracking-widest">Reference (UTR / Cheque)</Label>
              <Input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} className="rounded-sm mt-1 font-mono" />
            </div>
            <div className="md:col-span-1">
              <Label className="text-[10px] uppercase font-mono tracking-widest">&nbsp;</Label>
              <Button onClick={autoAllocate} variant="outline" className="w-full rounded-sm mt-1" data-testid="auto-allocate">Auto-allocate</Button>
            </div>
          </div>

          <div className="mt-6">
            <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2">Allocate to open {kind === "receipt" ? "invoices" : "bills"}</div>
            {selectedParty ? (
              openDocs.length === 0 ? (
                <div className="text-center text-muted-foreground py-6 border rounded-sm">No open documents — this receipt will be unallocated (on-account).</div>
              ) : (
                <div className="border rounded-sm overflow-hidden">
                  <table className="w-full dense">
                    <thead><tr><th>Doc #</th><th>Date</th><th className="text-right">Total</th><th className="text-right">Due</th><th className="text-right">Allocate</th></tr></thead>
                    <tbody>
                      {openDocs.map((d) => (
                        <tr key={d.id}>
                          <td className="font-mono text-xs">{d.doc_no}</td>
                          <td className="text-xs">{fmtDate(d.date)}</td>
                          <td className="text-right tabular-nums">{fmtINR(d.grand_total)}</td>
                          <td className="text-right tabular-nums font-medium">{fmtINR(d.balance_due)}</td>
                          <td className="text-right p-1">
                            <Input type="number" step="0.01" value={form.allocations[d.id] || ""} onChange={(e) => setAlloc(d.id, e.target.value)}
                                   className="rounded-sm h-8 w-32 text-right tabular-nums ml-auto" data-testid={`alloc-${d.doc_no}`} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="border-t bg-muted/40">
                      <tr>
                        <td colSpan="4" className="text-right py-2 px-3 text-sm">Allocated / Amount</td>
                        <td className={`text-right py-2 px-3 font-mono tabular-nums ${Math.abs(totalAlloc - Number(form.amount)) < 0.01 ? "text-emerald-600" : "text-warning"}`}>
                          {fmtINR(totalAlloc)} / {fmtINR(form.amount)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )
            ) : (
              <div className="text-center text-muted-foreground py-6 border rounded-sm">Select a party first.</div>
            )}
          </div>

          <div className="mt-6 flex justify-end gap-2">
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
            <Button variant="outline" onClick={saveAsDraft} className="rounded-sm" data-testid="payment-save-draft">{draftId ? "Update Draft" : "Save as Draft"}</Button>
            <Button onClick={submit} className="rounded-sm bg-accent hover:bg-accent/90 text-white" data-testid="payment-submit">Post {kind === "receipt" ? "Receipt" : "Payment"}</Button>
          </div>
        </SheetContent>
      </Sheet>

      {/* Iter-161 · Clickable drill-down for payment/receipt rows */}
      <Sheet open={!!detailRow} onOpenChange={(o) => !o && setDetailRow(null)}>
        <SheetContent side="right" className="w-full sm:max-w-lg rounded-none space-y-4" data-testid="payment-detail-sheet">
          <SheetHeader className="pb-3 border-b">
            <SheetTitle className="font-heading flex items-center gap-2">
              <Coins className="w-4 h-4 text-accent" />
              {detailRow?.kind === "receipt" ? "Receipt" : "Payment"} details
            </SheetTitle>
          </SheetHeader>
          {detailRow && (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div><div className="text-[10px] uppercase font-mono text-muted-foreground">Date</div><div>{fmtDate(detailRow.date)}</div></div>
                <div><div className="text-[10px] uppercase font-mono text-muted-foreground">Party</div><div>{detailRow.party_name}</div></div>
                <div><div className="text-[10px] uppercase font-mono text-muted-foreground">Amount</div><div className="tabular-nums font-semibold">{fmtINR(detailRow.amount)}</div></div>
                <div><div className="text-[10px] uppercase font-mono text-muted-foreground">Unallocated</div><div className="tabular-nums">{fmtINR(detailRow.unallocated)}</div></div>
                <div className="col-span-2"><div className="text-[10px] uppercase font-mono text-muted-foreground">Reference</div><div className="font-mono text-xs">{detailRow.reference || "—"}</div></div>
                {detailRow.narration && <div className="col-span-2"><div className="text-[10px] uppercase font-mono text-muted-foreground">Narration</div><div className="text-xs">{detailRow.narration}</div></div>}
              </div>
              {detailRow.allocations && detailRow.allocations.length > 0 && (
                <div>
                  <div className="text-[10px] uppercase font-mono text-muted-foreground mb-1">Allocations</div>
                  <div className="border rounded-sm overflow-hidden">
                    <table className="w-full dense">
                      <thead><tr><th>Doc</th><th className="text-right">Amount</th></tr></thead>
                      <tbody>
                        {detailRow.allocations.map((a, i) => (
                          <tr key={i}><td className="text-xs font-mono">{a.doc_type} · {a.doc_id?.slice(0,10) || "—"}</td><td className="text-right tabular-nums">{fmtINR(a.amount)}</td></tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              {detailVoucher && (
                <div>
                  <div className="text-[10px] uppercase font-mono text-muted-foreground mb-1">GL Voucher · {detailVoucher.voucher_number}</div>
                  <div className="border rounded-sm overflow-hidden">
                    <table className="w-full dense text-xs">
                      <thead><tr><th>Account</th><th className="text-right">Debit</th><th className="text-right">Credit</th></tr></thead>
                      <tbody>
                        {(detailVoucher.lines || []).map((ln, i) => (
                          <tr key={i}>
                            <td>{ln.account_name || ln.account_code || ln.account_id?.slice(0,10)}</td>
                            <td className="text-right tabular-nums">{ln.debit ? fmtINR(ln.debit) : ""}</td>
                            <td className="text-right tabular-nums">{ln.credit ? fmtINR(ln.credit) : ""}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
